import { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, IconButton, Modal, ModalClose, ModalDialog, ModalOverflow,
  Sheet, Stack, Textarea, Typography,
} from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";

import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import StarPicker from "./StarPicker";
import { fmtSize, isImage } from "./qualityTheme";

const MAX_FILES = 12;
const MAX_SIZE = 25 * 1024 * 1024;   // 25 МБ на файл

/**
 * Отправка чертежа на доработку.
 *
 * props:
 *   open, onClose, card  — карточка события
 *   onSubmit({ comment, stars, files }) → ожидает { status } от сервера
 */
export default function QualityReworkModal({ open, onClose, card, onSubmit }) {
  const [comment, setComment] = useState('');
  const [stars, setStars] = useState(0);
  const [files, setFiles] = useState([]);       // [{ file, preview }]
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => {
    if (open) {
      filesRef.current.forEach(item => { if (item.preview) URL.revokeObjectURL(item.preview); });
      setComment(''); setStars(0); setFiles([]); setError(''); setBusy(false);
    }
  }, [open]);

  // Превью живут в blob-URL. Без отзыва они держат память до перезагрузки
  // вкладки — на десятке скринов это уже заметно.
  useEffect(() => () => {
    filesRef.current.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview); });
  }, []);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const room = MAX_FILES - files.length;
    if (room <= 0) { setError(`Больше ${MAX_FILES} файлов не приложить`); return; }
    const tooBig = incoming.find((f) => f.size > MAX_SIZE);
    if (tooBig) { setError(`«${tooBig.name}» больше 25 МБ`); return; }
    setError('');
    setFiles((prev) => [
      ...prev,
      ...incoming.slice(0, room).map((file) => ({
        file,
        preview: isImage(file.type, file.name) ? URL.createObjectURL(file) : null,
      })),
    ]);
  };

  const removeAt = (i) => setFiles((prev) => {
    const f = prev[i];
    if (f?.preview) URL.revokeObjectURL(f.preview);
    return prev.filter((_, k) => k !== i);
  });

  // Ctrl+V прямо в окно: скрин проблемы чаще всего уже в буфере.
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const pics = items.filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter(Boolean);
    if (pics.length) { e.preventDefault(); addFiles(pics); }
  };

  const submit = async () => {
    const text = comment.trim();
    if (text.length < 5) {
      setError('Опишите, что не так — без этого чертёжник не поймёт, что править');
      return;
    }
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await onSubmit?.({ comment: text, stars, files: files.map((f) => f.file) });
      if (res?.status !== 'ok') { setError(res?.message || res?.detail || 'Не удалось отправить'); return; }
      onClose?.();
    } catch (failure) {
      setError('Не удалось отправить замечания. Текст и файлы сохранены в форме; проверьте связь и повторите.');
    } finally { setBusy(false); }
  };

  return (
    <Modal open={!!open} onClose={busy ? undefined : onClose} sx={{ zIndex: 10110 }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}>
      <ModalOverflow sx={{ p: { xs: 1.5, sm: 2 } }}>
        <ModalDialog variant="outlined" sx={{
          width: 640, maxWidth: '100%', p: 0,
          position: 'relative', top: 'auto', left: 'auto', my: 'auto',
          overflow: 'hidden',
        }}>
          <Box component={motion.div}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.2 }}
            onPaste={onPaste}
          >
            <Box sx={{
              px: { xs: 2, sm: 2.5 }, py: 2,
              background: 'linear-gradient(135deg, rgba(220,38,38,0.10), rgba(248,113,113,0.04))',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <ModalClose disabled={busy} />
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', pr: 4 }}>
                <ReplayRoundedIcon sx={{ color: '#dc2626' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="title-md" sx={{ fontWeight: 800 }}>
                    Отправить на доработку
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                    №{card?.deal_id ?? card?.id} · {card?.address || 'без адреса'}
                    {card?.draftsman?.name ? ` · ${card.draftsman.name}` : ''}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
              <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
                ЧТО НЕ ТАК
              </Typography>
              <Textarea
                minRows={4} maxRows={10}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={'Опишите конкретно: какие стены, размеры, слои.\n'
                  + 'Этот текст уйдёт чертёжнику и ляжет в чат разбора.'}
                sx={{ mb: 1.5 }}
                autoFocus
              />

              <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
                МАТЕРИАЛЫ
              </Typography>
              <Sheet
                variant="outlined"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); }}
                onClick={() => inputRef.current?.click()}
                sx={{
                  p: 1.5, borderRadius: '12px', cursor: 'pointer',
                  borderStyle: 'dashed', borderWidth: 2,
                  borderColor: dragOver ? 'primary.400' : 'neutral.outlinedBorder',
                  bgcolor: dragOver ? 'primary.softBg' : 'background.level1',
                  transition: 'all .15s', mb: 1.5,
                }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                  <AttachFileRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Перетащите скрины сюда, вставьте из буфера (Ctrl+V) или нажмите
                  </Typography>
                </Stack>
                <input
                  ref={inputRef} type="file" multiple hidden
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
              </Sheet>

              <AnimatePresence initial={false}>
                {files.length > 0 && (
                  <Box component={motion.div}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    sx={{ overflow: 'hidden', mb: 1.5 }}
                  >
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      {files.map((f, i) => (
                        <Box key={i} component={motion.div}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          sx={{ position: 'relative' }}
                        >
                          {f.preview ? (
                            <Box component="img" src={f.preview} alt={f.file.name}
                              sx={{
                                width: 74, height: 74, objectFit: 'cover', borderRadius: '10px',
                                border: '1px solid', borderColor: 'neutral.outlinedBorder', display: 'block',
                              }} />
                          ) : (
                            <Sheet variant="outlined" sx={{
                              width: 74, height: 74, borderRadius: '10px', p: 0.5,
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              justifyContent: 'center', gap: 0.25,
                            }}>
                              <InsertDriveFileRoundedIcon sx={{ fontSize: 20, color: 'text.tertiary' }} />
                              <Typography level="body-xs" sx={{ fontSize: '0.58rem', textAlign: 'center' }} noWrap>
                                {f.file.name}
                              </Typography>
                            </Sheet>
                          )}
                          <IconButton
                            size="sm" variant="solid" color="danger"
                            onClick={() => removeAt(i)}
                            sx={{
                              position: 'absolute', top: -6, right: -6,
                              '--IconButton-size': '20px', minWidth: 20, minHeight: 20,
                              borderRadius: '999px',
                            }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                          <Typography level="body-xs" sx={{
                            fontSize: '0.58rem', color: 'text.tertiary',
                            textAlign: 'center', mt: 0.25,
                          }}>
                            {fmtSize(f.file.size)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </AnimatePresence>

              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  ОЦЕНКА
                </Typography>
                <StarPicker value={stars} onChange={setStars} size={22} />
              </Stack>

              <Alert variant="soft" color="neutral" size="sm"
                startDecorator={<InfoOutlinedIcon />} sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                <Typography level="body-xs">
                  Комментарий и файлы уйдут чертёжнику пушем и лягут в чат разбора
                  по событию. В общий чат события попадёт короткая системная
                  строка — без подробностей, чтобы не мешать менеджерам.
                </Typography>
              </Alert>

              {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                  <Typography level="body-sm">{error}</Typography>
                </Alert>
              )}

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" color="neutral" onClick={onClose} disabled={busy}>
                  Отмена
                </Button>
                <Button color="danger" loading={busy} onClick={submit}
                  startDecorator={!busy ? <ReplayRoundedIcon /> : null}>
                  Отправить на доработку
                </Button>
              </Stack>
            </Box>
          </Box>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}
